// AAL-86: how a review entry reads in the UI (drawer list, in-form controls). Pure; `t` is the
// page's $t.

type Translate = (key: string) => unknown
type EntryLabels = { fieldKey: string, fieldLabel: string }

/** Ticket 19: below this confidence an operation is an assumption (the agent's prompt asks for
 * "Assumption:" reasons below 0.7); the drawer and the form controls label it for checking. */
export const ASSUMPTION_CONFIDENCE = 0.7

export function isAssumption(entry: { confidence?: number }): boolean {
	return typeof entry.confidence === 'number' && entry.confidence < ASSUMPTION_CONFIDENCE
}

export function hasValue(value: unknown): boolean {
	return value !== undefined && value !== null && value !== ''
}

/** Short plain-text form of a field value, an entry (its title or id) or a list item (its leaf values). */
export function displayValue(value: any): string {
	if (value === undefined || value === null || value === '') return '—'
	if (typeof value !== 'object') return String(value).replace(/<[^>]+>/g, '').slice(0, 160)
	if (typeof value.sys?.id === 'string') {
		const title = value.fields?.title
		const text = title && typeof title === 'object' ? Object.values(title).find(Boolean) : title
		return (text as string) ?? value.sys.id
	}
	if (value.fields && typeof value.fields === 'object') {
		return Object.entries(value.fields)
			.map(([ key, field ]: [ string, any ]) => [ key, field && typeof field === 'object' && 'de' in field ? field.de : field ])
			.filter(([ , leaf ]) => hasValue(leaf) && (typeof leaf !== 'object' || typeof leaf?.sys?.id === 'string'))
			.map(([ key, leaf ]) => `${key}: ${displayValue(leaf)}`)
			.join(', ')
	}
	return JSON.stringify(value).slice(0, 160)
}

/** First translation that exists wins, like Field.vue does for its labels. */
export function translateFirst(t: Translate, keys: string[], fallback: string): string {
	for (const key of keys) {
		const text = t('text.' + key)
		if (text && !String(text).startsWith('text.')) return String(text)
	}
	return fallback
}

export function fieldTitle(t: Translate, entry: EntryLabels): string {
	return translateFirst(t, [ `${entry.fieldLabel}_title`, `${entry.fieldKey}_title`, entry.fieldLabel, entry.fieldKey ], entry.fieldKey)
}
